import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Chance from 'chance';
import { PRODUCT_BOX_STYLE_TYPE, ProductBoxTypeType } from 'b2b-components/theme/components/ProductList';
import { useProductsAnalytics } from 'b2b-apps/core/analytics/context/products/ProductsAnalyticsContext';
import { RemoteRecord } from 'b2b-apps/shop/common/redux/RemoteRecord';
import { SOURCE_TYPE, VIEW_TYPE } from 'b2b-apps/shop/content/common';
import { useProductsFromQuery } from 'b2b-apps/shop/content/hooks';
import { extractSkuNumbersFromString } from 'b2b-apps/shop/content/utils/productsGroup';
import { useSid, useUserData } from 'b2b-apps/shop/context/hooks';
import { useProductTilesAction, useProductTilesData } from 'b2b-apps/shop/product/hooks';
import { ProductTileType } from 'b2b-apps/shop/product/redux/products/tile/types';
import { isProductGroupLoaded } from 'b2b-apps/shop/product/utils/product';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const MIN_COUNT_PRODUCTS_FOR_SLIDER = 4;
const HARD_PRODUCTS_LIMIT = 100;

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { productGroup } = props;
    const { isLoggedIn } = useUserData();
    const sid = useSid();

    const productsFromQuery = useProductsFromQuery(productGroup);
    const productTilesAction = useProductTilesAction();
    const {
      source_type: sourceType,
      source_of_products: sourceOfProducts,
      random_order: randomOrder,
      limit: limitStr,
      view,
      mpt_id: mptId,
      label,
    } = productGroup;

    const cmsLimit = parseInt(limitStr, 10);
    const limit = Math.min(cmsLimit, HARD_PRODUCTS_LIMIT);

    const getSkus = useCallback(
      (productsFromQuery: string[]): string[] => sourceType === SOURCE_TYPE.query
        ? productsFromQuery.slice(0, limit)
        : extractSkuNumbersFromString(sourceOfProducts),
      [limit, sourceType, sourceOfProducts],
    );

    const originalSkus = getSkus(productsFromQuery);
    const chance = new Chance(sid);
    const [skusToLoad, setSkusToLoad] = useState(originalSkus.slice(0, limit));
    const skus = randomOrder ? chance.shuffle(skusToLoad) : skusToLoad;
    const productTilesData = useProductTilesData(skus);
    const areProductTilesLoaded = Object
      .values(productTilesData)
      .every(productTileRecord => productTileRecord.isLoaded);
    const numberOfLoadedProducts = Object
      .values(productTilesData)
      .filter(productTileRecord => productTileRecord.data !== null)
      .length;

    useEffect(
      () => {
        if (areProductTilesLoaded && numberOfLoadedProducts < limit) {
          const difference = limit - numberOfLoadedProducts;

          setSkusToLoad(prevState => [
            ...prevState.filter(sku => productTilesData[sku].data !== null),
            ...originalSkus.slice(prevState.length, prevState.length + difference),
          ]);
        }
      },
      [areProductTilesLoaded, numberOfLoadedProducts, limit],
    );

    const getCountSliderLoadedProductTiles = useCallback(
      (
        productTilesData: Record<string, RemoteRecord<ProductTileType>>,
        skus: string[],
      ): number => {
        if (view !== VIEW_TYPE.slider) {
          return 0;
        }

        return skus.filter(
          (sku) => !productTilesData[sku]?.hasFailed
            && productTilesData[sku]?.isLoaded
            && productTilesData[sku]?.data,
        ).length;
      },
      [view],
    );

    const countSliderLoadedProductTiles = getCountSliderLoadedProductTiles(
      productTilesData,
      skus,
    );

    const getCountSliderLoadingProductTiles = useCallback(
      (
        productTilesData: Record<string, RemoteRecord<ProductTileType>>,
        skus: string[],
      ): number => {
        if (view !== VIEW_TYPE.slider) {
          return 0;
        }

        return skus.filter((sku) => productTilesData[sku]?.isLoading).length;
      },
      [view],
    );

    const countSliderLoadingProductTiles = getCountSliderLoadingProductTiles(
      productTilesData,
      skus,
    );

    const canCreateSlider = useMemo(
      (): boolean => countSliderLoadedProductTiles >= MIN_COUNT_PRODUCTS_FOR_SLIDER,
      [countSliderLoadedProductTiles],
    );

    const styleType = useMemo((): ProductBoxTypeType => {
      switch (view) {
        case VIEW_TYPE.list:
          return PRODUCT_BOX_STYLE_TYPE.list;

        case VIEW_TYPE.table:
          return PRODUCT_BOX_STYLE_TYPE.table;

        default:
          return PRODUCT_BOX_STYLE_TYPE.tile;
      }
    }, [view]);

    useEffect((): void => {
      if (
        view === VIEW_TYPE.slider
        && !isProductGroupLoaded(skus, productTilesData)
      ) {
        productTilesAction(skus, 0);
      }
    }, [productTilesAction, view, isLoggedIn, skus]);

    const { analyzeProductClick, analyzeProductAddToCart } = useProductsAnalytics();

    const injectedProps: InjectedProps = {
      countSliderLoadedProductTiles,
      isLoggedIn,
      isSlider:
        view === VIEW_TYPE.slider
        && (canCreateSlider || countSliderLoadingProductTiles > 0),
      label,
      mptId,
      skus,
      styleType,
      view,
      onProductClick: analyzeProductClick,
      onProductAddToCart: analyzeProductAddToCart,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};

export { withContainer };
